import Styles from './EnhanceBrandFonts.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight } from 'blaze-ui/icons'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { FontOption } from './FontOption/FontOption'
import { Text } from 'blaze-ui/Text/Text'
import VirtualMarketerOnboardingStyles from 'screens/VirtualMarketerOnboarding/VirtualMarketerOnboarding.module.scss'
import { useBrandKit } from 'hooks/queries/brandKits/useBrandKit'
import { useBrandVisualEnhancement } from 'hooks/queries/brandVisualEnhancements/useBrandVisualEnhancement'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { AIJobStatus, BrandKit, BrandVisualEnhancement, FontRole } from 'types'
import { usePreviewState } from '../PreviewProvider/PreviewProvider'
import { useUpdateBrandVisualEnhancement } from 'hooks/queries/brandVisualEnhancements/useUpdateBrandVisualEnhancement'
import { useUpsertTextStyle } from 'hooks/queries/workspaces/brandVisualKits/useUpsertTextStyle'
import { FontPairing } from 'constants/fontPairings'
import { FontWeightType } from 'components/VisualStudioPlayground/VisualEditor/Types'
import { LoadingAnimation } from '../BrandKitConfirm/LoadingAnimation'
import { useRootStore } from 'stores/RootStore'
import { useDestroyBrandVisualEnhancement } from 'hooks/queries/brandVisualEnhancements/useDestroyVisualEnancement'
import { TipCard } from '../Loading/TipCard'
import { useSanitiseFontConfig } from 'components/VisualStudioPlayground/VisualEditor/hooks/useSanitiseFontConfig'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

interface Props {
  onNext: ({
    isUsingEnhancedFonts,
    selectedEnhancedFontIndex,
  }: {
    isUsingEnhancedFonts: boolean
    selectedEnhancedFontIndex?: number | null
  }) => void
  onBack: () => void
  brandKitId: number
  brandVisualEnhancementId?: number
  selectedEnhancedFontIndex?: number | null
  fromBrandKitConfirm?: boolean
  brandKit?: BrandKit
  enhancement?: BrandVisualEnhancement
}

const findClosestWeight = (weights: FontWeightType[], targetWeight: number): FontWeightType => {
  return weights.reduce((prev, curr) => {
    return Math.abs(curr - targetWeight) < Math.abs(prev - targetWeight) ? curr : prev
  })
}

export const EnhanceBrandFontsWrapper = ({
  onNext,
  onBack,
  brandKitId,
  brandVisualEnhancementId,
  selectedEnhancedFontIndex,
  onEnhancementCreated,
  fromBrandKitConfirm,
  brandKit,
  enhancement,
}: Props & {
  onEnhancementCreated?: (brandVisualEnhancementId: number) => void
}) => {
  const { fonts } = useRootStore()
  const { data: brandKitResponse, isLoading: isBrandKitLoading } = useBrandKit(brandKitId, {
    pollUntilFinal: true,
    includeBrandVisualAnalysis: true,
    enabled: !fromBrandKitConfirm || !brandKit,
  })

  const finalBrandKit = brandKit || brandKitResponse?.brand_kit
  const brandKitStatus = finalBrandKit?.status as AIJobStatus | undefined
  const isFinal =
    brandKitStatus && [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired].includes(brandKitStatus)
  const isBrandKitAnalyzing = isBrandKitLoading || !isFinal

  const brandVisualKitId = finalBrandKit?.brand_visual_kit?.id

  const computedBrandVisualEnhancementId = finalBrandKit?.brand_visual_kit?.brand_visual_enhancement_id

  const { data: enhancementResponse, isLoading: enhLoading } = useBrandVisualEnhancement(
    brandVisualKitId,
    computedBrandVisualEnhancementId ?? undefined,
    {
      pollUntilFinal: true,
      enabled: !!computedBrandVisualEnhancementId && !!brandVisualKitId && (!fromBrandKitConfirm || !enhancement),
      keepPreviousData: true,
    },
  )

  const finalEnhancement = enhancement || enhancementResponse?.brand_visual_enhancement

  if (fromBrandKitConfirm && brandKit && enhancement) {
    return (
      <EnhanceBrandFonts
        onBack={onBack}
        onNext={onNext}
        brandKitId={brandKitId}
        brandVisualEnhancementId={computedBrandVisualEnhancementId ?? undefined}
        selectedEnhancedFontIndex={selectedEnhancedFontIndex}
        enhancement={enhancement}
        fromBrandKitConfirm={fromBrandKitConfirm}
        brandKit={brandKit}
      />
    )
  }

  const enhancementStatus = finalEnhancement?.status
  const enhancementLoading =
    computedBrandVisualEnhancementId &&
    (enhLoading || enhancementStatus === AIJobStatus.Pending || enhancementStatus === AIJobStatus.Started)

  if (
    isBrandKitLoading ||
    isBrandKitAnalyzing ||
    !computedBrandVisualEnhancementId ||
    !fonts.ready ||
    !finalEnhancement ||
    enhancementLoading
  ) {
    return (
      <>
        <LoadingAnimation handleBack={onBack} />
        <TipCard
          image="https://res.cloudinary.com/almanac/image/upload/v1754167659/virtual_marketer_onboarding/thumbnail2_b1npd4.png"
          title="Content Preferences"
          description="In your Brand Kit you can specify CTAs and words to avoid to finely tune your content."
        />
      </>
    )
  }

  if (!brandVisualKitId) {
    return (
      <MultiStepStepLayout
        title="Something went wrong"
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
        contentClassName={VirtualMarketerOnboardingStyles.content}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <Button variant="tertiary" size="xl" onPress={onBack} frontIcon={ArrowLeft}>
                  Back
                </Button>
                <Button size="xl" onPress={() => onNext({ isUsingEnhancedFonts: false })}>
                  Continue with current fonts
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={Styles.errorContainer}>
            <Text color="var(--dark-60)">
              We couldn't analyze your brand fonts. You can continue with your current fonts or go back and try again.
            </Text>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  return (
    <EnhanceBrandFonts
      onBack={onBack}
      onNext={onNext}
      brandKitId={brandKitId}
      brandVisualEnhancementId={computedBrandVisualEnhancementId ?? undefined}
      selectedEnhancedFontIndex={selectedEnhancedFontIndex}
      enhancement={finalEnhancement}
      fromBrandKitConfirm={fromBrandKitConfirm}
      brandKit={finalBrandKit}
    />
  )
}

const EnhanceBrandFonts = ({
  onNext,
  onBack,
  brandKitId,
  brandVisualEnhancementId,
  selectedEnhancedFontIndex,
  enhancement,
  fromBrandKitConfirm,
  brandKit,
}: Props & { enhancement: BrandVisualEnhancement }) => {
  const { isMobile } = useResponsiveDesign()
  const { setFontStyles, setBrandKit, setMedia } = usePreviewState()
  const initialSelectedOption = useMemo(
    () => (selectedEnhancedFontIndex !== null && selectedEnhancedFontIndex !== undefined ? 'enhanced' : 'original'),
    [selectedEnhancedFontIndex],
  )
  const [selectedOption, setSelectedOption] = useState<'enhanced' | 'original'>(initialSelectedOption)
  const [selectedIndex, setSelectedIndex] = useState<number | null>(selectedEnhancedFontIndex ?? 0)
  const [optimisticFonts, setOptimisticFonts] = useState<
    Record<string, { family: string; weight: FontWeightType; style: string } | undefined>
  >({})

  const {
    data: brandKitResponse,
    isLoading,
    isError,
    error,
    refetch,
  } = useBrandKit(brandKitId, {
    pollUntilFinal: !fromBrandKitConfirm,
    includeBrandVisualAnalysis: true,
    enabled: !fromBrandKitConfirm,
  })

  const finalBrandKit = brandKit || brandKitResponse?.brand_kit

  const brandVisualKitId = finalBrandKit?.brand_visual_kit?.id

  const { data: liveEnhancementResponse } = useBrandVisualEnhancement(
    brandVisualKitId,
    brandVisualEnhancementId ?? undefined,
    {
      enabled: !!brandVisualEnhancementId && !!brandVisualKitId,
      keepPreviousData: true,
    },
  )

  const activeEnhancement = liveEnhancementResponse?.brand_visual_enhancement || enhancement

  const brandKitStatus = finalBrandKit?.status as AIJobStatus | undefined
  const isFinal =
    brandKitStatus && [AIJobStatus.Successful, AIJobStatus.Failed, AIJobStatus.Expired].includes(brandKitStatus)
  const showLoader = !fromBrandKitConfirm && (isLoading || !isFinal)
  const hasFailed = brandKitStatus === AIJobStatus.Failed
  const { mutate: destroyEnhancement } = useDestroyBrandVisualEnhancement(brandVisualKitId, {
    onSettled: () => {
      onBack?.()
    },
  })
  const sanitiseFontConfig = useSanitiseFontConfig()

  useEffect(() => {
    if (finalBrandKit) {
      setBrandKit(finalBrandKit)
      setMedia(finalBrandKit.media_files[0])
    }
  }, [finalBrandKit, setBrandKit, setMedia])

  const textStyles = finalBrandKit?.brand_visual_kit?.text_styles
  const getTextStyle = useCallback((role: FontRole) => textStyles?.find((s: any) => s.font_role === role), [textStyles])

  useEffect(() => {
    if (!textStyles) return
    const updates: Record<string, undefined> = {}
    Object.entries(optimisticFonts).forEach(([key, font]) => {
      if (font) {
        const role = key === 'title' ? FontRole.Title : FontRole.Body
        const style = getTextStyle(role)
        if (style?.style.font_family === font.family && style?.style.font_weight === font.weight) {
          updates[key] = undefined
        }
      }
    })
    if (Object.keys(updates).length > 0) {
      setOptimisticFonts((prev) => ({ ...prev, ...updates }))
    }
  }, [textStyles, optimisticFonts, getTextStyle])

  const { mutate: updateEnhancement } = useUpdateBrandVisualEnhancement(brandVisualKitId, brandVisualEnhancementId)

  const { mutateAsync: upsertTitle } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Title,
  })
  const { mutateAsync: upsertBody } = useUpsertTextStyle({
    brandVisualKitId: brandVisualKitId as number,
    fontRole: FontRole.Body,
  })

  const persistEnhancedFont = (role: FontRole, family: string, weight: FontWeightType, index: number) => {
    if (!brandVisualEnhancementId) return
    const key = role === FontRole.Title ? 'display_font' : 'body_font'
    const sanitisedConfig = sanitiseFontConfig({ fontFamily: family, weight, italic: false })
    updateEnhancement({
      result: {
        ...activeEnhancement?.result,
        suggested_font_pairings: suggestions?.map((suggestion, i) => {
          if (i === index) {
            return {
              ...suggestion,
              [key]: {
                family: sanitisedConfig.fontFamily,
                style: sanitisedConfig.italic ? 'italic' : 'normal',
                weight: sanitisedConfig.weight,
                reasoning: suggestion.reasoning,
              },
            }
          }
          return suggestion
        }),
      },
    })
  }

  const persistOriginalFont = async (role: FontRole, family: string, weights: FontWeightType[]) => {
    const isTitle = role === FontRole.Title
    const fontKey = isTitle ? 'title' : 'body'
    const currentData = getFontData(fontKey)
    const weight = findClosestWeight(weights, currentData.weight)
    const sanitisedConfig = sanitiseFontConfig({ fontFamily: family, weight, italic: false })

    setOptimisticFonts((prev) => ({
      ...prev,
      [fontKey]: {
        family: sanitisedConfig.fontFamily,
        weight: sanitisedConfig.weight,
        style: sanitisedConfig.italic ? 'italic' : 'normal',
      },
    }))

    try {
      const payload = {
        style: {
          font_family: sanitisedConfig.fontFamily,
          font_weight: sanitisedConfig.weight,
          font_size: isTitle ? 24 : 16,
          italic: sanitisedConfig.italic,
          line_height: 1,
          letter_spacing: -40,
        },
      }
      await (isTitle ? upsertTitle(payload) : upsertBody(payload))
      refetch()
    } catch (error) {
      setOptimisticFonts((prev) => ({ ...prev, [fontKey]: undefined }))
      throw error
    }
  }

  /* ───────── extract fonts ──────── */
  const suggestions = activeEnhancement?.result?.suggested_font_pairings
  const selectedSuggestion = suggestions?.[selectedIndex ?? 0]

  const getFontData = useCallback(
    (key: 'title' | 'body') => {
      const role = key === 'title' ? FontRole.Title : FontRole.Body
      const style = getTextStyle(role)
      const defaultWeight = key === 'title' ? 700 : 400
      const family = optimisticFonts[key]?.family ?? style?.style.font_family ?? ''
      const weight = optimisticFonts[key]?.weight ?? style?.style.font_weight ?? defaultWeight
      const fontStyle = optimisticFonts[key]?.style ?? (style?.style.italic ? 'italic' : 'normal')

      return {
        family,
        weight,
        style: fontStyle,
        preview:
          selectedOption === 'enhanced'
            ? selectedSuggestion?.[key === 'title' ? 'display_font' : 'body_font']?.family || family
            : family,
      }
    },
    [getTextStyle, optimisticFonts, selectedOption, selectedSuggestion],
  )

  const titleData = useMemo(() => getFontData('title'), [getFontData])
  const bodyData = useMemo(() => getFontData('body'), [getFontData])

  const previewPairing: FontPairing | undefined = useMemo(() => {
    if (!titleData.preview || !bodyData.preview) return
    const isEnhanced = selectedOption === 'enhanced'
    const titleWeight = (isEnhanced && selectedSuggestion?.display_font?.weight) || titleData.weight
    const bodyWeight = (isEnhanced && selectedSuggestion?.body_font?.weight) || bodyData.weight
    return [
      {
        font_role: FontRole.Title,
        style: { font_family: titleData.preview, font_weight: titleWeight as FontWeightType },
      },
      { font_role: FontRole.Body, style: { font_family: bodyData.preview, font_weight: bodyWeight as FontWeightType } },
    ]
  }, [titleData, bodyData, selectedOption, selectedSuggestion])

  useEffect(() => {
    if (activeEnhancement?.status !== AIJobStatus.Successful) return
    if (!previewPairing) return
    setFontStyles(previewPairing)
  }, [activeEnhancement?.status, previewPairing, setFontStyles])

  const status = activeEnhancement?.status
  const failed = brandVisualEnhancementId && status === AIJobStatus.Failed

  if (failed) {
    return (
      <MultiStepStepLayout
        title="Something went wrong"
        scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
        contentClassName={VirtualMarketerOnboardingStyles.content}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter>
              <FooterButtons>
                <Button
                  variant="tertiary"
                  size="xl"
                  onPress={() => destroyEnhancement({ id: brandVisualEnhancementId as number })}
                  frontIcon={ArrowLeft}
                >
                  Back
                </Button>
                <Button size="xl" onPress={() => onNext({ isUsingEnhancedFonts: false })}>
                  Continue with current fonts
                </Button>
              </FooterButtons>
            </StickyFooter>
          }
        >
          <div className={Styles.errorContainer}>
            <Text color="var(--dark-60)">
              We couldn't analyze your brand fonts. You can continue with your current fonts or go back and try again.
            </Text>
          </div>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  }

  return (
    <MultiStepStepLayout
      title="Select your fonts for social posts"
      scrollContainerClassName={VirtualMarketerOnboardingStyles.scrollContainer}
      contentClassName={VirtualMarketerOnboardingStyles.content}
    >
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                {onBack ? (
                  <Button
                    id="enhance-brand-fonts-back"
                    variant="tertiary"
                    size="xl"
                    onPress={() => {
                      setSelectedOption(initialSelectedOption)
                      setSelectedIndex(selectedEnhancedFontIndex ?? null)
                      onBack()
                    }}
                    frontIcon={ArrowLeft}
                  >
                    Back
                  </Button>
                ) : (
                  <div />
                )}
              </ResponsiveDesign.Desktop>
              <Button
                id="enhance-brand-fonts-next"
                size="xl"
                endIcon={ArrowRight}
                isDisabled={showLoader || hasFailed || isError}
                onPress={() =>
                  onNext({
                    isUsingEnhancedFonts: selectedOption === 'enhanced',
                    selectedEnhancedFontIndex: selectedIndex,
                  })
                }
                fullWidth={isMobile}
              >
                Confirm Font
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        <div className={Styles.root}>
          {(isError || hasFailed) && (
            <div className={Styles.error}>
              {error instanceof Error ? error.message : 'Failed to populate brand kit.'}
            </div>
          )}
          <div className={Styles.fontOptions}>
            {/* Original */}
            {titleData.family && bodyData.family && (
              <FontOption
                titleFontFamily={titleData.family}
                bodyFontFamily={bodyData.family}
                titleFontWeight={titleData.weight}
                bodyFontWeight={bodyData.weight}
                titleFontStyle={titleData.style}
                bodyFontStyle={bodyData.style}
                title="Here's what we pulled from your site."
                badgeText="Your Website"
                ordinal={1}
                isFromWebsite
                isSelected={selectedOption === 'original'}
                onSelect={() => {
                  setSelectedOption('original')
                  setSelectedIndex(null)
                }}
                onFontChange={(role, family, weights) => {
                  persistOriginalFont(role, family, weights)
                }}
              />
            )}

            {/* Enhanced */}
            {suggestions?.map((suggestion, index) => {
              const titleFontFamily = suggestion.display_font?.family ?? ''
              const bodyFontFamily = suggestion.body_font?.family ?? ''
              const titleFontWeight = (suggestion.display_font?.weight ?? titleData.weight) as FontWeightType
              const bodyFontWeight = (suggestion.body_font?.weight ?? bodyData.weight) as FontWeightType
              const titleFontStyle = suggestion.display_font?.style ?? titleData.style
              const bodyFontStyle = suggestion.body_font?.style ?? bodyData.style

              let title = 'This font pairing feels just right for your brand'
              if (index === 1) title = 'This font pairing also feels like a good match.'
              if (index === 2) title = 'This font pairing feels like a great option to explore.'

              return (
                <React.Fragment key={index}>
                  {titleFontFamily && bodyFontFamily && (
                    <FontOption
                      titleFontFamily={titleFontFamily}
                      bodyFontFamily={bodyFontFamily}
                      titleFontWeight={titleFontWeight}
                      bodyFontWeight={bodyFontWeight}
                      titleFontStyle={titleFontStyle}
                      bodyFontStyle={bodyFontStyle}
                      title={title}
                      badgeText={index === 0 ? 'Best Option' : undefined}
                      ordinal={index + 2}
                      description={suggestion.reasoning}
                      isSelected={selectedOption === 'enhanced' && selectedIndex === index}
                      onSelect={() => {
                        setSelectedOption('enhanced')
                        setSelectedIndex(index)
                      }}
                      onFontChange={(role, family, weights) => {
                        const currentSuggestion = suggestions?.[index]
                        const targetWeight =
                          role === FontRole.Title
                            ? currentSuggestion?.display_font?.weight ?? 700
                            : currentSuggestion?.body_font?.weight ?? 400
                        persistEnhancedFont(
                          role,
                          family,
                          findClosestWeight(weights, targetWeight as FontWeightType),
                          index,
                        )
                        if (role === FontRole.Title)
                          titleFontFamily && previewPairing && (previewPairing[0].style.font_family = family)
                        else bodyFontFamily && previewPairing && (previewPairing[1].style.font_family = family)
                      }}
                    />
                  )}
                </React.Fragment>
              )
            })}
          </div>
        </div>
      </StickyFooterProvider>
    </MultiStepStepLayout>
  )
}

export { EnhanceBrandFontsWrapper as EnhanceBrandFonts }
